import React, { useState } from "react";
import * as dmsService from "../../services/dmsService";
import MasterSection from "../Platform/setup/MasterSection";
import AccessSetup from "../Platform/setup/AccessSetup";
import NumberingSetup from "../Platform/setup/NumberingSetup";
import WorkflowSetup from "../Platform/setup/WorkflowSetup";
import useFinOptions from "../Fin/useFinOptions";
import WorkshopSetup from "./setup/WorkshopSetup";
import { AftercareSetup, FiSetup, FleetInsurersSetup, TargetsSetup } from "./setup/MoreSetup";
import { CampaignsSetup, CatalogueSetup, ChecklistsSetup, OrderTypesSetup, PriceListsSetup, SalesTeamSetup, ServicesSetup } from "./setup/SalesSetup";
import { AppDropdown, DetailGrid, FormDrawer, LoadState, PageHeader, Tabs, cardStyle, iconBtnStyle, pageStyle, useLoad } from "../../components/ui";

// Dealership setup (Dealership admin). P0: System Options, the pick lists (follow-up
// modes, lost reasons ...), document numbering, approvals and access. Later phases add
// the vehicle catalogue, order types, price lists and checklists here.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };

function SystemOptions() {
  const { data, loading, error, reload } = useLoad(() => dmsService.getSettings(), []);
  const opts = useFinOptions(["rate-types"]);
  const [editing, setEditing] = useState(false);
  const s = data || {};
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ ...cardStyle, padding: 20, maxWidth: 720 }}>
        <div className="d-flex justify-content-between mb-3">
          <h6 className="mb-0">System options</h6>
          <button type="button" style={linkBtn} onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
        <DetailGrid
          items={[
            ["Quotation valid for", s.quote_validity_days ? `${s.quote_validity_days} days` : null],
            ["Chassis reservation lapses after", s.reservation_expiry_hours ? `${s.reservation_expiry_hours} hours (unless the order is booked)` : null],
            ["Only PDI-done chassis can be reserved", s.pdi_required_to_reserve ? "Yes" : "No"],
            ["Discount needing approval", s.discount_approval_pct !== undefined ? `Above ${Number(s.discount_approval_pct)}%` : null],
            ["Minimum advance to book an order", s.min_advance_pct !== undefined ? `${Number(s.min_advance_pct)}%` : null],
            ["Default exchange rate type", s.default_rate_type],
          ]}
        />
      </div>
      <FormDrawer
        show={editing}
        title="System options"
        initial={{ ...s }}
        onHide={() => setEditing(false)}
        onSave={(clean, raw) =>
          dmsService.saveSettings({
            quote_validity_days: Number(clean.quote_validity_days),
            reservation_expiry_hours: Number(clean.reservation_expiry_hours),
            pdi_required_to_reserve: !!raw.pdi_required_to_reserve,
            discount_approval_pct: Number(clean.discount_approval_pct),
            min_advance_pct: Number(clean.min_advance_pct),
            default_rate_type: clean.default_rate_type,
          })
        }
        onDone={reload}
        fields={[
          { key: "quote_validity_days", label: "Quotation valid for (days)", type: "number", min: 1, required: true },
          { key: "reservation_expiry_hours", label: "Reservation lapses after (hours)", type: "number", min: 1, required: true },
          { key: "pdi_required_to_reserve", label: "Only chassis with PDI done can be reserved", type: "checkbox" },
          { key: "discount_approval_pct", label: "Discount above this % needs approval", type: "number", step: "any", min: 0, required: true },
          { key: "min_advance_pct", label: "Minimum advance to book an order (%)", type: "number", step: "any", min: 0, required: true },
          { key: "default_rate_type", label: "Default exchange rate type", type: "select", options: (opts["rate-types"] || []).map((o) => ({ label: o.label, value: o.code })) },
        ]}
      />
    </LoadState>
  );
}

function Lists() {
  const types = useLoad(() => dmsService.getLookupTypes(), []);
  const [type, setType] = useState("FOLLOWUP_MODE");
  const typeOptions = types.data || [];
  return (
    <>
      <div className="mb-3" style={{ maxWidth: 320 }}>
        <AppDropdown value={type} options={typeOptions} onChange={(e) => setType(e.value)} aria-label="List" />
      </div>
      <MasterSection
        key={type}
        noun="value"
        deps={[type]}
        load={() => dmsService.listLookups({ lookup_type: type })}
        // A cleared "order" keeps the list's default position rather than writing an empty value.
        save={(id, data) => {
          const { sort_order: order, ...rest } = data;
          const body = order === null || order === undefined || order === "" ? rest : { ...rest, sort_order: Number(order) };
          return dmsService.saveLookup(id, id ? body : { ...body, lookup_type: type });
        }}
        columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Order", (r) => r.sort_order]]}
        fieldKeys={["name", "sort_order"]}
        lockedOnEdit={["code"]}
        fields={() => [
          { key: "code", label: "Code", required: true, max: 32, hint: "Letters, digits, - and _" },
          { key: "name", label: "Name", required: true, max: 100 },
          { key: "sort_order", label: "Order in the list", type: "number", min: 0 },
        ]}
      />
    </>
  );
}

const TABS = [
  { key: "options", label: "System options", render: () => <SystemOptions /> },
  { key: "catalogue", label: "Vehicle catalogue", render: () => <CatalogueSetup /> },
  { key: "ordertypes", label: "Order types", render: () => <OrderTypesSetup /> },
  { key: "prices", label: "Price lists", render: () => <PriceListsSetup /> },
  { key: "services", label: "Services offered", render: () => <ServicesSetup /> },
  { key: "campaigns", label: "Campaigns", render: () => <CampaignsSetup /> },
  { key: "checklists", label: "Checklists", render: () => <ChecklistsSetup /> },
  { key: "team", label: "Sales team", render: () => <SalesTeamSetup /> },
  { key: "workshop", label: "Workshop", render: () => <WorkshopSetup /> },
  { key: "aftercare", label: "After-sales care", render: () => <AftercareSetup /> },
  { key: "fi", label: "F&I", render: () => <FiSetup /> },
  { key: "fleet", label: "Fleet & insurers", render: () => <FleetInsurersSetup /> },
  { key: "targets", label: "Sales targets", render: () => <TargetsSetup /> },
  { key: "lists", label: "Lists", render: () => <Lists /> },
  { key: "numbering", label: "Document numbering", render: () => <NumberingSetup module="dms" /> },
  { key: "approvals", label: "Approvals", render: () => <WorkflowSetup module="dms" /> },
  { key: "access", label: "Access", render: () => <AccessSetup module="dms" label="the Dealership" /> },
];

export default function DmsSetup() {
  const [tab, setTab] = useState("options");
  const active = TABS.find((t) => t.key === tab);
  return (
    <div style={pageStyle}>
      <PageHeader title="Dealership Setup" subtitle="Catalogue, prices, order types, checklists, numbering, approvals and access for the dealership" />
      <Tabs tabs={TABS.map(({ key, label }) => ({ key, label }))} active={tab} onChange={setTab} />
      {active.render()}
    </div>
  );
}
